import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client';

import { useDeps } from 'src/hooks/useDeps';
import { paths } from 'src/config.json';
import { Branch } from 'src/components/GoogleMaps/GoogleMapsTypes';

import { branchLocations } from '../../queries/__generated__/branchLocations';
import getBranchLocations from '../../queries/getBranchLocations.graphql';
import { stripDomain } from '../../util/sanitizeLink';

type BranchLocationQueryContextType = {
    branches: Branch[];
    loading: boolean;
    error: ApolloError | undefined;
};

export const BranchLocationQueryContext = React.createContext<BranchLocationQueryContextType>({
    branches: [],
    loading: false,
    error: undefined,
});

export const BranchLocationQueryProvider = (props) => {
    const { error, data, loading } = useQuery<branchLocations, any>(getBranchLocations, {
        ssr: useDeps().env.SSR_GQL,
    });

    const api: BranchLocationQueryContextType = (() => {
        if (error) {
            return { loading: false, branches: [], error };
        }
        if (loading) {
            return { loading, branches: [], error: undefined };
        }
        if (data && data.branchLocations && cleanBranches(data.branchLocations)) {
            return { loading: false, error: undefined, branches: cleanBranches(data.branchLocations) };
        }
        return { loading, branches: [], error: undefined };
    })();

    return <BranchLocationQueryContext.Provider value={api}>{props.children}</BranchLocationQueryContext.Provider>;
};

export function cleanBranches(branches: branchLocations['branchLocations']): Branch[] {
    return branches.map((branch) => {
        if (!branch) {
            return {
                name: 'unknown',
                url: 'unknown',
                latitude: 0,
                longitude: 0,
                telephone: 'unknown',
                county: 'unknown',
                address: 'unknown',
                city: 'unknown',
                state: 'unknown',
                id: 'unknown',
            };
        }

        const url = stripDomain(branch?.url);
        const id = url.replace(`${paths.storeLocator.storeLocator}/`, '');

        return {
            name: branch.name,
            county: branch.city,
            telephone: branch.phone ?? '',
            latitude: Number(branch.latitude),
            longitude: Number(branch.longitude),
            url,
            address: branch.address,
            city: branch.city,
            state: branch.state ?? null,
            id,
            distance_km: 0,
            distance_miles: 0,
        };
    });
}
